/**
 * API Client for FieldVoice backend
 */

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface Transcript {
  id: string;
  transcript_text: string;
  timestamp: number;
  gps_lat: number | null;
  gps_lon: number | null;
  gps_accuracy: number | null;
  created_at: number;
  enhanced_text: string | null;
  enhancement_model: string | null;
  enhanced_at: number | null;
}

export interface EnhancementResult {
  enhanced_text: string;
  model?: string;
  status: 'completed' | 'already_enhanced';
}

export interface DeviceHealth {
  account_id: string;
  device_id: string;
  last_seen: number;
  app_version: string;
  os_version: string;
  battery_level: number | null;
  is_online: boolean;
}

export interface AccountFlags {
  transcription_mode: string;
  retention_days: string;
  upload_only_on_wifi: string;
  speaker_verification_preset: string;
  llm_enhancement_enabled: string;
  [key: string]: string;
}

export interface SystemMetrics {
  active_devices: number;
  transcripts_today: number;
  transcripts_total: number;
  avg_latency_ms: number;
  error_rate: number;
  active_connections: number;
}

export interface QuotaUsage {
  transcription: { used: number; limit: number };
  api_requests: { used: number; limit: number };
  llm_enhancements: { used: number; limit: number };
}

export interface SoakTestResult {
  test_id: string;
  device_id: string;
  started_at: number;
  updated_at: number;
  duration_ms: number;
  status: 'running' | 'completed' | 'passed' | 'failed';
  passed: boolean;
  memory: {
    avg_heap_mb: number;
    max_heap_mb: number;
    heap_growth_mb: number;
    native_heap_mb: number;
    native_growth_mb: number;
  };
  battery: {
    start_level: number;
    end_level: number;
    drain_per_hour: number;
  };
  processing: {
    chunks_processed: number;
    chunks_uploaded: number;
    chunks_failed: number;
    audio_gaps: number;
  };
  speaker_gate: {
    accepts: number;
    rejects: number;
  };
  reliability: {
    service_restarts: number;
    crash_count: number;
    error_count: number;
  };
}

export interface SoakSummary {
  period_days: number;
  total_tests: number;
  passed_tests: number;
  pass_rate: number;
  metrics: {
    avg_heap_mb: number;
    avg_battery_drain_per_hour: number;
    total_audio_gaps: number;
    total_service_restarts: number;
    total_crashes: number;
  };
}

// Soak Test Control Panel Types
export interface SoakTestConfig {
  duration_hours: number;
  audio_interval_minutes: number;
  channels: string[];
  environment: 'development';
}

export interface SoakTestDevice {
  device_id: string;
  status: 'healthy' | 'warning' | 'error' | 'offline';
  battery_level: number;
  is_screen_on: boolean;
  is_plugged_in: boolean;
  last_seen: number;
  transcripts_received: number;
}

export interface SoakTestStatus {
  test_id: string | null;
  status: 'idle' | 'running' | 'completed' | 'failed';
  started_at: number | null;
  duration_hours: number;
  elapsed_ms: number;
  metrics: {
    devices_active: number;
    transcripts_sent: number;
    transcripts_received: number;
    avg_latency_ms: number;
    errors: number;
    crashes: number;
    missing_transcripts: number;
  };
  devices: SoakTestDevice[];
}

export interface SoakLog {
  id: string;
  timestamp: number;
  severity: 'INFO' | 'WARN' | 'ERROR' | 'CRITICAL';
  source: 'orchestrator' | 'android' | 'cloudflare';
  message: string;
  device_id?: string;
  metadata?: Record<string, unknown>;
}

export type SoakLogSeverity = 'ALL' | 'INFO' | 'WARN' | 'ERROR' | 'CRITICAL';
export type SoakLogSource = 'all' | 'orchestrator' | 'android' | 'cloudflare';

class ApiClient {
  private token: string = '';

  setToken(token: string) {
    this.token = token;
  }

  getToken(): string {
    return this.token;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(`${API_BASE}${path}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });

      if (response.status === 401) {
        return { success: false, error: 'Unauthorized' };
      }

      return response.json();
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  // Auth
  async validateToken() {
    return this.request<{ account_id: string; valid: boolean }>('GET', '/v1/auth/validate');
  }

  // Health
  async health() {
    return this.request<{ status: string }>('GET', '/v1/health');
  }

  // Transcripts
  async getTranscripts(
    params: {
      limit?: number;
      offset?: number;
      startDate?: number;
      endDate?: number;
    } = {}
  ) {
    const { limit = 50, offset = 0, startDate, endDate } = params;
    let url = `/v1/transcripts?limit=${limit}&offset=${offset}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;

    return this.request<{
      transcripts: Transcript[];
      pagination: { limit: number; offset: number; total?: number };
    }>('GET', url);
  }

  async searchTranscripts(query: string) {
    return this.request<{ transcripts: Transcript[] }>(
      'GET',
      `/v1/transcripts/search?q=${encodeURIComponent(query)}`
    );
  }

  async getTranscript(id: string) {
    return this.request<{
      transcript: Transcript;
      enhancement_status: 'completed' | 'pending';
    }>('GET', `/api/transcripts/${id}`);
  }

  async enhanceTranscript(id: string) {
    return this.request<EnhancementResult>('POST', `/api/transcripts/${id}/enhance`);
  }

  async exportTranscripts(params: {
    format: 'json' | 'csv';
    startDate?: number;
    endDate?: number;
  }) {
    const { format, startDate, endDate } = params;
    let url = `/v1/transcripts/export?format=${format}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;

    // For export, we return raw response
    const headers: Record<string, string> = {
      Authorization: `Bearer ${this.token}`,
    };

    const response = await fetch(`${API_BASE}${url}`, { headers });
    return response;
  }

  // Flags
  async getFlags() {
    return this.request<{ flags: AccountFlags }>('GET', '/v1/flags');
  }

  async updateFlags(flags: Partial<AccountFlags>) {
    return this.request<{ updated: string[] }>('PATCH', '/v1/flags', flags);
  }

  // Admin - Account flags
  async getAccountFlags(accountId: string) {
    return this.request<{ flags: AccountFlags }>('GET', `/v1/admin/accounts/${accountId}/flags`);
  }

  async updateAccountFlags(accountId: string, flags: Partial<AccountFlags>) {
    return this.request<{ updated: string[] }>(
      'PATCH',
      `/v1/admin/accounts/${accountId}/flags`,
      flags
    );
  }

  // Admin - Device health
  async getDeviceHealth(accountId?: string) {
    const url = accountId ? `/v1/admin/devices?account_id=${accountId}` : '/v1/admin/devices';
    return this.request<{ devices: DeviceHealth[] }>('GET', url);
  }

  // Admin - All accounts (for admin view)
  async getAccounts() {
    return this.request<{ accounts: { id: string; created_at: number }[] }>(
      'GET',
      '/v1/admin/accounts'
    );
  }

  // Admin - Retention settings
  async getRetentionSettings() {
    return this.request<{ retention_days: number; default: number }>('GET', '/v1/admin/retention');
  }

  async updateRetentionSettings(days: number) {
    return this.request<{ updated: boolean }>('PATCH', '/v1/admin/retention', {
      retention_days: days,
    });
  }

  // Admin - System metrics
  async getMetrics() {
    return this.request<{ metrics: SystemMetrics }>('GET', '/v1/admin/metrics');
  }

  // Admin - Quota usage
  async getQuotaUsage() {
    return this.request<{ quota: QuotaUsage }>('GET', '/v1/admin/quota');
  }

  // Soak test results
  async getSoakResults(params: { device_id?: string; status?: string; limit?: number } = {}) {
    let url = '/api/soak/results';
    const queryParams: string[] = [];
    if (params.device_id) queryParams.push(`device_id=${params.device_id}`);
    if (params.status) queryParams.push(`status=${params.status}`);
    if (params.limit) queryParams.push(`limit=${params.limit}`);
    if (queryParams.length > 0) url += '?' + queryParams.join('&');

    return this.request<{ results: SoakTestResult[] }>('GET', url);
  }

  async getSoakSummary(days: number = 7) {
    return this.request<SoakSummary>('GET', `/api/soak/summary?days=${days}`);
  }

  async getLatestSoakTest(deviceId: string) {
    return this.request<SoakTestResult>('GET', `/api/soak/latest/${deviceId}`);
  }

  // Soak Test Control Panel Methods
  async startSoakTest(config: SoakTestConfig) {
    return this.request<{ test_id: string }>('POST', '/api/soak/start', config);
  }

  async stopSoakTest() {
    return this.request<void>('POST', '/api/soak/stop');
  }

  async getSoakTestStatus() {
    return this.request<SoakTestStatus>('GET', '/api/soak/status');
  }

  async getSoakLogs(params: {
    limit?: number;
    offset?: number;
    severity?: SoakLogSeverity;
    source?: SoakLogSource;
    test_id?: string;
    search?: string;
  } = {}) {
    const queryParams: string[] = [];
    if (params.limit) queryParams.push(`limit=${params.limit}`);
    if (params.offset) queryParams.push(`offset=${params.offset}`);
    if (params.severity && params.severity !== 'ALL') queryParams.push(`severity=${params.severity}`);
    if (params.source && params.source !== 'all') queryParams.push(`source=${params.source}`);
    if (params.test_id) queryParams.push(`test_id=${params.test_id}`);
    if (params.search) queryParams.push(`search=${encodeURIComponent(params.search)}`);

    const url = '/api/soak/logs' + (queryParams.length > 0 ? '?' + queryParams.join('&') : '');
    return this.request<{ logs: SoakLog[]; total: number }>('GET', url);
  }

  async downloadSoakLogs(testId: string): Promise<Blob> {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${this.token}`,
    };
    const response = await fetch(`${API_BASE}/api/soak/logs/download?test_id=${testId}`, { headers });
    return response.blob();
  }

  // WebSocket URL for real-time soak logs
  getSoakLogsWebSocketUrl(): string {
    const wsBase = API_BASE.replace('http://', 'ws://').replace('https://', 'wss://');
    return `${wsBase}/api/soak/logs/stream?token=${this.token}`;
  }
}

export const apiClient = new ApiClient();
